import { QuerySnapshot, DocumentSnapshot } from 'firebase/firestore';
import FBInterface from './fbinterface';
import {User} from 'firebase/auth';
import {TaskCreateStatus, TaskDataMeta, TaskDataTableRow, TaskStatus, getEmptyTasksData} from '../pages/tasks/tasks'
import { ContentSetData, ContentStatus, getEmptyContentData } from '../pages/content/content';

const USER_TASKS_COL:string='usertasks/';
const USER_CONTENT_COL:string='contentsets/';
const CONTENT_AUTOINC_DOC:string='contentAutoId/counter';

const CONTENT_STORAGE_BASE:string='https://firebasestorage.googleapis.com/v0/b/gamestolearnenglish-fb.appspot.com/o/contentsets%2F';
const JSON_TAIL='.json?alt=media';
const JSON_DASH:string='%2F';

export interface MyUserType{
	username:string;
	usermail:string;
}

interface TaskResults{
	[key:string]:{score:number;	isComplete:boolean;	}
}

interface MyDoc{
	[key: string]: any
}

const DELETE_META:boolean=true;
const NO_DELETE_META:boolean=false;

export default class FBWrapper{

	private usermail:string='';
	private fBInterface=new FBInterface();

	//////////////////////////////////////////////////////////////////////////////////
	//login
	
	addAuthListener(callback:Function){
		this.fBInterface.setInterfacePersistence();
		this.fBInterface.addAuthListener((user:User|null)=>{
			if(user!==null){
				if(user.email)this.usermail=user.email;
				callback({username:user.displayName, usermail:user.email});
			}else{
				callback({username:'', usermail:''});
			}
		});
	}

	signIn(callback:Function){
		this.fBInterface.signIn((err:string)=>{
			if(err!=='')console.log('Sign in Error =',err);
			callback(err);
		})
	}

	signOut(callback:Function){
		this.fBInterface.signOut((err:string)=>{
			if(err!=='')console.log('Sign out Error =',err);
			callback(err);
		})
	}

	//////////////////////////////////////////////////////////////////////////////////
	//tasks
	getTaskList(callback:Function){
		this.fBInterface.getOneDoc(USER_TASKS_COL+this.usermail,(err:string,tasksData:MyDoc)=>{
			let tasksList:string[]=[];
			if(tasksData.tasks!==undefined)tasksList=tasksData.tasks;
			if(err==='err'){
				callback(TaskStatus.GetListError,tasksList);
			}else{
				callback(TaskStatus.Default,tasksList);
			}
		})
	}

	getTaskData(taskName:string,callback:Function){
		let taskResultsList:TaskResults={};
		let myMeta:TaskDataMeta|null=null;
		let scoreType='';
		this.fBInterface.getAllDocs(USER_TASKS_COL+this.usermail+'/'+taskName,(res:QuerySnapshot|null)=>{
			if(res!==null){
				res.forEach((doc:DocumentSnapshot)=> {
					if(doc.id==='meta'){
						myMeta=this.processTaskMeta(doc);
						scoreType=myMeta.scoreType;
					}else{
						taskResultsList=this.addTaskResult(doc, taskResultsList);
					}
				});
				if(myMeta===null){//no meta but task in taskslist
					callback(TaskStatus.GetTaskMetaError,getEmptyTasksData());
				}else{
					let taskTableData=this.prepareTaskTableData(taskResultsList, scoreType);
					callback(TaskStatus.Default,{tableData:taskTableData,metaData:myMeta});
				}
			}else{
				callback(TaskStatus.GetTaskDataError,getEmptyTasksData());
			}
		});
	}

	resetTaskData(taskTitle:string,callback:Function){
		this.taskDeleter(taskTitle,NO_DELETE_META,(err:number)=>{
			if(err===0){
				callback(TaskStatus.Default);
			}else{
				callback(TaskStatus.ResetTaskFailed);
			}
		})
	}

	deleteTask(taskTitle:string,tasksList:string[],callback:Function){
		let newTasksList=tasksList.slice();
		newTasksList.splice(tasksList.indexOf(taskTitle),1);
		this.taskDeleter(taskTitle,DELETE_META,(err:number)=>{
			if(err===0){//now need to update users tasksList
				this.fBInterface.setDocument(USER_TASKS_COL+this.usermail,{tasks:newTasksList},(err:number)=>{
					if(err===0){
						callback(TaskStatus.Default,newTasksList);
					}else{
						callback(TaskStatus.DeleteTaskFailed,tasksList);
					}
				});
			}else{
				callback(TaskStatus.DeleteTaskFailed,tasksList);
			}
		})
	}

	createTask(taskData:TaskDataMeta, tasksList:string[], callback:Function){
		let newTasksList:string[]=tasksList.slice();
		newTasksList.push(taskData.taskTitle);//here also get and push id
		this.fBInterface.setDocument(USER_TASKS_COL+this.usermail,{tasks:newTasksList},(err:number)=>{
			if(err===0){
				this.fBInterface.setDocument(USER_TASKS_COL+this.usermail+'/'+taskData.taskTitle+'/meta',taskData,(err:number)=>{
					if(err==0){
						callback(TaskCreateStatus.Success,newTasksList);
					}else{
						callback(TaskCreateStatus.FailedDelete,newTasksList);
					}
				});
			}else{
				callback(TaskCreateStatus.Failed,tasksList);
			}
		});
	}

	private taskDeleter(taskTitle:string,deleteMeta:boolean,callback:Function){
		let deleteList:string[]=[];
		this.fBInterface.getAllDocs(USER_TASKS_COL+this.usermail+'/'+taskTitle,(res:QuerySnapshot|null)=>{
			if(res!==null){
				res.forEach((myDoc)=>{
					if(myDoc.id!=='meta'||deleteMeta){
						deleteList.push(USER_TASKS_COL+this.usermail+"/"+taskTitle+"/"+myDoc.id);
					}
				});
				this.fBInterface.deleteDocuments(deleteList,(outcome:boolean)=>{
					if(outcome){
						callback(0);
					}else{
						callback(1);
					}
				})
			}else{
				callback(1);
			}
		})
	}

	//////////////////////////////////////////////////////////////////////////////////
	//content

	getContentList(callback:Function){
		this.fBInterface.getOneDoc(USER_CONTENT_COL+this.usermail,(err:string,contentData:MyDoc)=>{
			if(err==='err'){
				callback(ContentStatus.GetListError,[]);
			}else{
				let mySets:string[]=[];
				if(err!=='noexist'){
					Object.keys(contentData).forEach((myKey)=>{
						contentData[myKey].forEach((contentSet:string)=>{
							mySets.push(contentSet);
						})
					})
				}
				callback(ContentStatus.Default,mySets);
			}
		})
	}

	getContentSet(contentSet:string, callback:Function){
		let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
		let myPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+contentSet+JSON_TAIL;
		this.fBInterface.getJson(myPath,(err:number, res:string)=>{
			if(err===0){
				callback(ContentStatus.Default,this.processContentData(contentSet,res));
			}else{
				let myEmptySet=getEmptyContentData();
				myEmptySet.title=contentSet;
				callback(ContentStatus.GetSetError,myEmptySet);
			}
		})
	}

	saveContentSet(contentSetData:ContentSetData, userContentList:string[], callback:Function){
		this.addToUserContentList(userContentList, contentSetData.title, (outcome:boolean, newList:string[])=>{
			if(outcome===false){
				callback(ContentStatus.SaveSetError, userContentList,-1);
			}else{
				let myUpload = JSON.parse(JSON.stringify(contentSetData));//copys by value
				this.getContentAutoIncrement(contentSetData.myID,(newID:number)=>{
					myUpload.myID=newID;
					let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
					let myPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+contentSetData.title+JSON_TAIL;
					let myJson=JSON.stringify(myUpload);
					let jsonBlob:Blob=new Blob([myJson]);
					this.fBInterface.uploadJson(myPath, jsonBlob, (err:number)=>{
						if(err===0){
							callback(ContentStatus.Default, newList, newID);
						}else{
							callback(ContentStatus.SaveSetError, newList, newID);
						}
					});
				})

			}
		})
	}

	deleteContentSet(setTitle:string, userContentList:string[], callback:Function){
		//only return error if fail to remove from list
		this.deleteFromUserContentList(userContentList, setTitle, (outcome:number, newList:string[])=>{
			if(outcome===0){
				let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
				let jsonPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+setTitle+JSON_TAIL;
				this.fBInterface.deleteJson(jsonPath, (err:number)=>{
					callback(ContentStatus.Default,newList);
				})
			}else{
				callback(ContentStatus.DeleteSetError,userContentList);
			}
		})
	}

	// private getContentAutoIncrement(curID:number, callback:Function){
	getContentAutoIncrement(curID:number, callback:Function){
		if(curID!==-1){
			callback(curID);
		}else{
			this.fBInterface.incrementCounter(CONTENT_AUTOINC_DOC,(err:number)=>{
				if(err===0){
					this.fBInterface.getOneDoc(CONTENT_AUTOINC_DOC,(err:string, docData:{id:number})=>{
						if(err===''){
							callback(docData.id);
						}else{
							callback(-1);
						}
					})
				}else{
					callback(-1);
				}
			});
		}
	}

	private addToUserContentList(userContentList:string[], newTitle:string, callback:Function){
		if(userContentList.indexOf(newTitle)!==-1){
			callback(true,userContentList);
		}else{
			let newList=userContentList.slice();
			newList.push(newTitle);
			this.fBInterface.setDocument(USER_CONTENT_COL+this.usermail,{setsList:newList},(err:number)=>{
				if(err===0){
					callback(true,newList);
				}else{
					callback(false,newList);
				}
			});
		}
	}

	private deleteFromUserContentList(userContentList:string[], deleteTitle:string, callback:Function){
		if(userContentList.indexOf(deleteTitle)===-1){
			callback(0,userContentList);
		}else{
			let newList=userContentList.slice();
			newList.splice(newList.indexOf(deleteTitle),1);
			this.fBInterface.setDocument(USER_CONTENT_COL+this.usermail,{setsList:newList},(err:number)=>{
				if(err===0){
					callback(0,newList);
				}else{
					callback(1,[]);
				}
			});
		}
	}

	processContentData(contentSet:string, res:string){
		let parsedRes=JSON.parse(res);
		let myContentData=getEmptyContentData();
		myContentData.title=contentSet;
		if(parsedRes.imsList!==undefined)myContentData.imsList=parsedRes.imsList;
		if(parsedRes.txtsList!==undefined)myContentData.txtsList=parsedRes.txtsList;
		if(parsedRes.options!==undefined)myContentData.options=parsedRes.options;
		if(parsedRes.myID!==undefined)myContentData.myID=parsedRes.myID;
		return myContentData;
	}

	////////////////////////////////////////////////////////////////////
	//process data for one task

	addTaskResult(doc:DocumentSnapshot, taskResultsList:TaskResults){
		let myUser='';
		let myScore=-1;
		if(doc.data()?.user)myUser=doc.data()?.user;
		if(doc.data()?.score)myScore=doc.data()?.score;
		let myUsername=myUser.replace(/[^a-zA-Z0-9]/g, '');
		if(doc.data()?.completed===undefined){
			if(taskResultsList[myUsername]===undefined){
				taskResultsList[myUsername]={score:-1, isComplete:false};
			}
		}else{
			let higherScore=myScore;
			if(taskResultsList[myUsername]!==undefined){
				if(taskResultsList[myUsername].score>higherScore){
					higherScore=taskResultsList[myUsername].score;
				}
			}
			taskResultsList[myUsername]={score:higherScore,isComplete:true};
		}
		return taskResultsList;
	}

	prepareTaskTableData(taskResultsList:TaskResults, scoreType:string){
		let sortType='desc';
		if(scoreType==='moves')sortType='asc';
		if(scoreType==='time')sortType='asc';

		let keys = Object.keys(taskResultsList);
		if(sortType==='asc'){
			keys.sort(function(a, b) { 
				if(taskResultsList[a].isComplete===false)return 1;
				if(taskResultsList[b].isComplete===false)return -1;
				return (taskResultsList[a].score - taskResultsList[b].score);
			})
		}else{
			keys.sort(function(a, b) { 
				return (taskResultsList[b].score - taskResultsList[a].score);
			})
		}

		let taskTableData:TaskDataTableRow[]=[];
		for(let i=0;i<keys.length;i++){
			taskTableData[i]={
				rank:i+1,
				name:keys[i],
				complete:taskResultsList[keys[i]].isComplete?'Yes':'No',
				score:taskResultsList[keys[i]].score
			}
		}
		return taskTableData;
	}

	processTaskMeta(inMeta:DocumentSnapshot):TaskDataMeta{
		return {
			contentType: inMeta.data()?.contentType||'',
			contentTitle: inMeta.data()?.contentTitle||'',
			creatorMail: inMeta.data()?.creatorMail||'',
			taskTitle: inMeta.data()?.taskTitle||'',
			scoreType: inMeta.data()?.scoreType||'',
			pageDir: inMeta.data()?.pageDir||'',
			taskOptions: inMeta.data()?.taskOptions||[]
		};
	}
}
